/**
 * Potion System - Health and Mana potions with charge-based usage
 */

export interface PotionConfig {
  maxCharges: number;
  chargePerUse: number;
  normalKillCharge: number;
  rareKillCharge: number;
  bossKillCharge: number;
}

export interface PotionState {
  currentCharges: number;
  maxCharges: number;
}

const DEFAULT_CONFIG: PotionConfig = {
  maxCharges: 100,
  chargePerUse: 25,
  normalKillCharge: 1,
  rareKillCharge: 10,
  bossKillCharge: 25,
};

export class PotionSystem {
  private healthPotion: PotionState;
  private manaPotion: PotionState;
  private config: PotionConfig;

  // Cooldown to prevent spam
  private healthCooldown: number = 0;
  private manaCooldown: number = 0;
  private readonly COOLDOWN_TIME = 0.5; // seconds

  constructor(config: Partial<PotionConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    this.healthPotion = {
      currentCharges: this.config.maxCharges,
      maxCharges: this.config.maxCharges,
    };

    this.manaPotion = {
      currentCharges: this.config.maxCharges,
      maxCharges: this.config.maxCharges,
    };
  }

  /**
   * Calculate heal amount based on player level
   * Level 1: 50 HP, Level 70: 1000 HP (linear scaling)
   */
  calculateHealAmount(playerLevel: number): number {
    const baseHeal = 50;
    const maxHeal = 1000;
    const maxLevel = 70;

    // Linear interpolation
    const t = Math.min(playerLevel - 1, maxLevel - 1) / (maxLevel - 1);
    return Math.floor(baseHeal + t * (maxHeal - baseHeal));
  }

  /**
   * Calculate mana restore amount based on player level
   * Level 1: 30 Mana, Level 70: 500 Mana
   */
  calculateManaAmount(playerLevel: number): number {
    const baseMana = 30;
    const maxMana = 500;
    const maxLevel = 70;

    const t = Math.min(playerLevel - 1, maxLevel - 1) / (maxLevel - 1);
    return Math.floor(baseMana + t * (maxMana - baseMana));
  }

  /**
   * Use health potion
   * Returns the amount healed, or 0 if couldn't use
   */
  useHealthPotion(playerLevel: number): number {
    if (this.healthCooldown > 0) return 0;
    if (this.healthPotion.currentCharges < this.config.chargePerUse) return 0;

    this.healthPotion.currentCharges -= this.config.chargePerUse;
    this.healthCooldown = this.COOLDOWN_TIME;

    return this.calculateHealAmount(playerLevel);
  }

  /**
   * Use mana potion
   * Returns the amount of mana restored, or 0 if couldn't use
   */
  useManaPotion(playerLevel: number): number {
    if (this.manaCooldown > 0) return 0;
    if (this.manaPotion.currentCharges < this.config.chargePerUse) return 0;

    this.manaPotion.currentCharges -= this.config.chargePerUse;
    this.manaCooldown = this.COOLDOWN_TIME;

    return this.calculateManaAmount(playerLevel);
  }

  /**
   * Add charges when killing enemies
   */
  onEnemyKill(isRare: boolean, isBoss: boolean): void {
    let chargeAmount = this.config.normalKillCharge;

    if (isBoss) {
      chargeAmount = this.config.bossKillCharge;
    } else if (isRare) {
      chargeAmount = this.config.rareKillCharge;
    }

    // Add to both potions
    this.healthPotion.currentCharges = Math.min(
      this.healthPotion.maxCharges,
      this.healthPotion.currentCharges + chargeAmount
    );

    this.manaPotion.currentCharges = Math.min(
      this.manaPotion.maxCharges,
      this.manaPotion.currentCharges + chargeAmount
    );
  }

  /**
   * Update cooldowns
   */
  update(deltaTime: number): void {
    if (this.healthCooldown > 0) {
      this.healthCooldown -= deltaTime;
    }
    if (this.manaCooldown > 0) {
      this.manaCooldown -= deltaTime;
    }
  }

  /**
   * Get health potion state
   */
  getHealthPotionState(): PotionState {
    return { ...this.healthPotion };
  }

  /**
   * Get mana potion state
   */
  getManaPotionState(): PotionState {
    return { ...this.manaPotion };
  }

  /**
   * Get number of uses remaining
   */
  getHealthPotionUses(): number {
    return Math.floor(this.healthPotion.currentCharges / this.config.chargePerUse);
  }

  getManaPotionUses(): number {
    return Math.floor(this.manaPotion.currentCharges / this.config.chargePerUse);
  }

  /**
   * Check if health potion can be used
   */
  canUseHealthPotion(): boolean {
    return this.healthCooldown <= 0 && this.healthPotion.currentCharges >= this.config.chargePerUse;
  }

  /**
   * Check if mana potion can be used
   */
  canUseManaPotion(): boolean {
    return this.manaCooldown <= 0 && this.manaPotion.currentCharges >= this.config.chargePerUse;
  }

  /**
   * Refill potions (e.g., when visiting town)
   */
  refillPotions(): void {
    this.healthPotion.currentCharges = this.healthPotion.maxCharges;
    this.manaPotion.currentCharges = this.manaPotion.maxCharges;
  }

  /**
   * Serialize for saving
   */
  serialize(): object {
    return {
      healthCharges: this.healthPotion.currentCharges,
      manaCharges: this.manaPotion.currentCharges,
    };
  }

  /**
   * Deserialize from save
   */
  deserialize(data: { healthCharges?: number; manaCharges?: number }): void {
    if (data.healthCharges !== undefined) {
      this.healthPotion.currentCharges = data.healthCharges;
    }
    if (data.manaCharges !== undefined) {
      this.manaPotion.currentCharges = data.manaCharges;
    }
  }
}
